import React from 'react';
import { Link } from 'react-router-dom';
import { CaretLeft, ShieldCheck, Lock, Eye, Trash, Download, Database, Clock } from '@phosphor-icons/react';

/**
 * Public privacy notice. Linked from the landing page footer + referenced
 * from the GDPR endpoints in /app/account. Plain English, no legalese
 * theatre — just the actual things we do, the actual things we don't,
 * and the named GDPR rights with a direct link to the user's account page.
 *
 * If you change anything here, also update the matching marketing line
 * in Landing.jsx and the help drawer.
 */
export default function Privacy() {
    return (
        <div className="min-h-screen bg-white text-[#09090B]">
            <header className="border-b border-[#18181B] sticky top-0 bg-white z-30">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Link
                        to="/"
                        data-testid="privacy-back-link"
                        className="p-2 -ml-2 hover:bg-[#F4F4F5] inline-flex items-center"
                        aria-label="Back to home"
                    >
                        <CaretLeft size={20} weight="bold" />
                    </Link>
                    <img
                        src="/defectr-logo.png"
                        alt="DefectR"
                        className="h-8 w-auto object-contain"
                    />
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[#52525B]">
                        Privacy &amp; Data
                    </span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10" data-testid="privacy-page">
                <section>
                    <div className="flex items-center gap-3 mb-3">
                        <ShieldCheck size={24} weight="duotone" color="#1FA5CF" />
                        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                            Privacy &amp; security
                        </h1>
                    </div>
                    <p className="text-base text-[#52525B] leading-relaxed">
                        DefectR is built for new-build homeowners walking their plot at completion.
                        Your defect list is sensitive — it can affect a warranty claim, a sale,
                        or a deposit. We treat it that way.
                    </p>
                    <p className="text-xs font-mono uppercase tracking-[0.18em] text-[#A1A1AA] mt-4">
                        Last updated: 1 May 2026
                    </p>
                </section>

                {/* What we do */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <h2 className="font-display text-xl font-semibold mb-3">What we collect — and why</h2>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li><strong>Your name, email, and Google profile picture</strong> — to identify your account when you sign in.</li>
                        <li><strong>Property details, defect notes, photos, and floorplans</strong> — to power the snag-list and report features. We never read or browse them; only you and anyone you explicitly share with do.</li>
                        <li><strong>Stripe payment-receipt metadata</strong> (session ID, amount, currency, status, the property the unlock was for) — needed for refund rights, accounting, and dispute resolution. <strong>We never see or store your card number, CVV, or expiry.</strong> Stripe handles every card field directly.</li>
                        <li><strong>Login session info</strong> (browser, IP, time) — for security and to let you sign out from "everywhere" if needed.</li>
                        <li><strong>An audit log of account-level actions</strong> (account export, deletion, sign-out-everywhere). Required for incident response and dispute resolution.</li>
                    </ul>
                </section>

                {/* What we don't do */}
                <section className="border-l-4 border-[#00875A] pl-5">
                    <h2 className="font-display text-xl font-semibold mb-3">What we don&apos;t do</h2>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>We <strong>don&apos;t sell or share</strong> your data to advertisers or data brokers.</li>
                        <li>We <strong>don&apos;t train AI</strong> on your photos. The Gemini snag-analysis API is called per-defect, with your prompt only, and the response is cached against that single defect.</li>
                        <li>We <strong>don&apos;t track you across the web</strong>. No third-party trackers, no fingerprinting.</li>
                        <li>We <strong>don&apos;t store your card number</strong>. Stripe Checkout handles everything; we only see &quot;paid / pending / failed&quot;.</li>
                        <li>We <strong>don&apos;t read your magic-link emails</strong> or scrape your inbox.</li>
                    </ul>
                </section>

                {/* Your rights */}
                <section>
                    <div className="flex items-center gap-3 mb-3">
                        <Lock size={20} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold">Your rights — and how to use them</h2>
                    </div>
                    <p className="text-sm text-[#52525B] mb-5">
                        Under UK GDPR you have rights over your data. We&apos;ve baked the three
                        most important ones directly into the app — no email, no waiting, no
                        support ticket. Open your <Link to="/app/account" className="underline font-semibold text-[#1787AB]">account page</Link> and use them whenever you want.
                    </p>

                    <div className="grid sm:grid-cols-3 gap-3">
                        <RightCard
                            icon={<Clock size={18} weight="duotone" color="#1FA5CF" />}
                            title="Sign out everywhere"
                            body="Invalidates every active session on every device — useful if you suspect your Google account was compromised."
                            tag="Instant"
                        />
                        <RightCard
                            icon={<Download size={18} weight="duotone" color="#1FA5CF" />}
                            title="Export your data"
                            body="Download a full JSON copy of everything we hold against your account. GDPR Article 20."
                            tag="GDPR Art. 20"
                        />
                        <RightCard
                            icon={<Trash size={18} weight="duotone" color="#E53935" />}
                            title="Delete your account"
                            body="Erase your account and every property, defect, and payment record. Cannot be undone. GDPR Article 17."
                            tag="GDPR Art. 17"
                        />
                    </div>
                </section>

                {/* Where it's stored */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Database size={18} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold">Where your data is stored</h2>
                    </div>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>
                            <strong>Database</strong> — Mongo, hosted on Emergent. Defect records,
                            account info, and audit logs.
                        </li>
                        <li>
                            <strong>Object storage</strong> — Photos and floorplans, encrypted in
                            transit, served only via authenticated proxy URLs.
                        </li>
                        <li>
                            <strong>Email</strong> — Magic links and digest emails sent via Resend.
                            We never read responses.
                        </li>
                        <li>
                            <strong>Payments</strong> — Stripe (PCI-DSS Level 1). We see no card data.
                        </li>
                        <li>
                            <strong>AI analysis</strong> — Defect title + notes + photo (when present)
                            sent per-request to Google Gemini. Result is cached against the specific
                            defect; never used for training.
                        </li>
                    </ul>
                </section>

                {/* Security */}
                <section className="border-l-4 border-[#1FA5CF] pl-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Eye size={18} weight="duotone" color="#1FA5CF" />
                        <h2 className="font-display text-xl font-semibold">Security at a glance</h2>
                    </div>
                    <ul className="space-y-3 text-sm text-[#09090B]">
                        <li>HTTPS everywhere. HSTS, strict CSP, X-Frame-Options=DENY, no third-party scripts.</li>
                        <li>Session cookies are <code className="text-xs bg-[#F4F4F5] px-1">httpOnly</code> + <code className="text-xs bg-[#F4F4F5] px-1">Secure</code> + <code className="text-xs bg-[#F4F4F5] px-1">SameSite=Lax</code>.</li>
                        <li>Magic-link tokens are single-use, 15-minute TTL, ~256-bit entropy.</li>
                        <li>Rate limits on share, AI, file proxy, and login endpoints to slow abuse.</li>
                        <li>Image uploads validated by magic bytes — HTML / SVG disguised as &quot;image/jpeg&quot; is rejected.</li>
                        <li>Dependencies scanned weekly via Dependabot; SAST run on every commit via Semgrep.</li>
                    </ul>
                </section>

                {/* Contact */}
                <section>
                    <h2 className="font-display text-xl font-semibold mb-3">Questions or concerns?</h2>
                    <p className="text-sm text-[#52525B]">
                        Email <a href="mailto:support@defectr.co.uk" className="underline font-semibold">support@defectr.co.uk</a>{' '}
                        — we aim to respond within 2 working days. For data-protection requests
                        specifically, please use <strong>&quot;DPR&quot;</strong> as the subject prefix
                        so your email is routed correctly.
                    </p>
                </section>
            </main>

            <footer className="border-t border-[#18181B]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between text-xs font-mono uppercase tracking-[0.2em] text-[#52525B]">
                    <span>© DefectR Ltd · Co. No. 09929002</span>
                    <div className="flex items-center gap-4">
                        <Link to="/terms" className="underline hover:text-[#1787AB]" data-testid="privacy-footer-terms-link">
                            Terms &amp; refunds
                        </Link>
                        <Link to="/" className="underline" data-testid="privacy-footer-home-link">
                            Back to home
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function RightCard({ icon, title, body, tag }) {
    return (
        <div className="border border-[#E4E4E7] p-4 hover:border-[#18181B] transition-all duration-150">
            <div className="flex items-center gap-2 mb-2">
                {icon}
                <h3 className="font-display font-semibold text-sm flex-1">{title}</h3>
            </div>
            <p className="text-xs text-[#52525B] mb-2">{body}</p>
            <span className="inline-block font-mono text-[9px] uppercase tracking-[0.18em] text-[#A1A1AA] border border-[#E4E4E7] px-1.5 py-0.5">
                {tag}
            </span>
        </div>
    );
}
